import { catmullRom2Bezier, generateWavyPoints } from './utils.js';

let cache = {};
const highlighter = document.getElementById('line-highlighter');
const svgLayer = document.getElementById('svg-layer');

function moveHighlight(line) {
    if (line === null) { highlighter.style.opacity = 0; return; }
    const lineHeight = 21;
    const paddingTop = 16;
    highlighter.style.opacity = 1;
    highlighter.style.top = `${(line - 1) * lineHeight + paddingTop}px`;
}

async function travel(from, to, color = "#ff4757") {
    return new Promise(resolve => {
        const r1 = from.getBoundingClientRect();
        const r2 = to.getBoundingClientRect();
        const sX = window.scrollX;
        const sY = window.scrollY;
        const p1 = { x: r1.left + sX + r1.width / 2, y: r1.top + sY + r1.height / 2 };
        const p2 = { x: r2.left + sX + r2.width / 2, y: r2.top + sY + r2.height / 2 };
        const pathData = catmullRom2Bezier([p1, ...generateWavyPoints(p1, p2, 2, 40), p2]);
        const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
        path.setAttribute("d", pathData);
        path.setAttribute("stroke", color);
        path.setAttribute("fill", "none");
        path.setAttribute("stroke-width", "2");
        path.setAttribute("opacity", "0.3");
        svgLayer.appendChild(path);
        const dot = document.createElement('div');
        dot.className = 'moving-dot';
        dot.style.background = color;
        document.body.appendChild(dot);
        let prog = 0;
        const len = path.getTotalLength();
        function frame() {
            prog += 0.025;
            if (prog <= 1) {
                const pt = path.getPointAtLength(prog * len);
                dot.style.left = `${pt.x - 6}px`;
                dot.style.top = `${pt.y - 6}px`;
                requestAnimationFrame(frame);
            } else {
                dot.remove();
                setTimeout(() => path.remove(), 500);
                resolve();
            }
        }
        frame();
    });
}

function internalFibFast(n) {
    if (n in cache) return cache[n];
    if (n === 0) { cache[0] = 0; updateCacheUI(); return 0; }
    if (n === 1) { cache[0] = 0; cache[1] = 1; updateCacheUI(); return 1; }
    cache[n] = internalFibFast(n - 1) + internalFibFast(n - 2);
    updateCacheUI();
    return cache[n];
}

async function internalFibDetailed(n) {
    const monitorStatus = document.getElementById('monitor-status');
    const monitorAction = document.getElementById('monitor-action');
    const stackContainer = document.getElementById('stack-container');
    const leftEl = document.getElementById('left-branch');
    const rightEl = document.getElementById('right-branch');

    const stackItem = document.createElement('div');
    stackItem.className = 'stack-item';
    stackItem.innerText = `fib(${n}) - Pending`;
    stackContainer.appendChild(stackItem);

    moveHighlight(15);
    monitorStatus.innerText = `Level ${stackContainer.children.length}: n = ${n}`;
    monitorAction.innerText = `Entering fib(${n})...`;
    await new Promise(r => setTimeout(r, 200));

    moveHighlight(16);
    if (n in cache) {
        monitorAction.innerText = `HIT! Using cached value: ${cache[n]}`;
        await new Promise(r => setTimeout(r, 300));
        stackContainer.removeChild(stackItem);
        return cache[n];
    }

    moveHighlight(17);
    if (n === 0) { cache[0] = 0; updateCacheUI(); stackContainer.removeChild(stackItem); return 0; }
    moveHighlight(18);
    if (n === 1) { cache[0] = 0; cache[1] = 1; updateCacheUI(); stackContainer.removeChild(stackItem); return 1; }

    moveHighlight(23);
    leftEl.innerText = `fib(${n - 1})`;
    leftEl.className = "branch-slot active-branch";
    rightEl.innerText = `fib(${n - 2})`;
    const val1 = await internalFibDetailed(n - 1);

    moveHighlight(23);
    leftEl.innerText = val1;
    leftEl.className = "branch-slot resolved-branch";
    rightEl.className = "branch-slot active-branch";
    const val2 = await internalFibDetailed(n - 2);

    moveHighlight(24);
    rightEl.innerText = val2;
    rightEl.className = "branch-slot resolved-branch";
    const res = val1 + val2;
    cache[n] = res;
    updateCacheUI();
    await new Promise(r => setTimeout(r, 300));
    stackContainer.removeChild(stackItem);
    return res;
}

async function run() {
    const inputEl = document.getElementById('arg-input');
    const n = parseInt(inputEl.value);
    const showDetails = document.getElementById('recursion-toggle').checked;

    if (showDetails && n > 31) {
        alert("Recursion details are limited to n <= 31.");
        return;
    }

    document.getElementById('check-n').innerText = n;
    document.getElementById('heavy-n').innerText = n;

    const checkBox = document.getElementById('check-box');
    const heavyBox = document.getElementById('heavy-func-box');
    const processor = document.getElementById('processor');
    const cacheBox = document.getElementById('cache-box');
    const returnBox = document.getElementById('return-box');
    const recBox = document.getElementById('recursion-details');

    moveHighlight(5);
    await travel(inputEl, checkBox);

    if (cache[n] !== undefined) {
        moveHighlight(6);
        await travel(checkBox, cacheBox, "#2ecc71");
        await travel(cacheBox, returnBox, "#2ecc71");
        document.getElementById('return-val').innerText = `Fib(${n}) = ${cache[n]}`;
    } else {
        document.getElementById('ProcessingMsg').innerText = " ...Updating Cache...";
        moveHighlight(9);
        await travel(checkBox, heavyBox, "#e74c3c");
        processor.classList.add('processing');

        // Allow DOM to update (show text and start rotation)
        await new Promise(r => setTimeout(r, 50));

        let res;
        if (showDetails) {
            await travel(heavyBox, recBox, "#e67e22");
            res = await internalFibDetailed(n);
        } else {
            res = internalFibFast(n);
        }

        document.getElementById('ProcessingMsg').innerText = "";
        processor.classList.remove('processing');

        moveHighlight(10);
        await travel(showDetails ? recBox : heavyBox, cacheBox, "#9b59b6");
        updateCacheUI();

        moveHighlight(11);
        await travel(cacheBox, returnBox, "#2ecc71");
        document.getElementById('return-val').innerText = `Fib(${n}) = ${res}`;
    }
    setTimeout(() => moveHighlight(null), 1000);
}

function updateCacheUI() {
    const container = document.getElementById('cache-contents');
    const scrollArea = document.getElementById('cache-scroll-area');
    container.innerHTML = Object.entries(cache)
        .sort((a, b) => a[0] - b[0])
        .map(([k, v]) => `<div class="cache-entry">"${k}": ${v}</div>`).join('');
    scrollArea.scrollTop = scrollArea.scrollHeight;
}

function resetAll() {
    cache = {};
    updateCacheUI();
    document.getElementById('return-val').innerText = "---";
    document.getElementById('ProcessingMsg').innerText = "";
    document.getElementById('left-branch').innerText = "fib(n-1)";
    document.getElementById('right-branch').innerText = "fib(n-2)";
    document.getElementById('left-branch').className = "branch-slot";
    document.getElementById('right-branch').className = "branch-slot";
    document.getElementById('stack-container').innerHTML = "";
    document.getElementById('monitor-status').innerText = "Standing by...";
    document.getElementById('monitor-action').innerText = "";
    svgLayer.innerHTML = "";
    moveHighlight(null);
    document.getElementById('processor').classList.remove('processing');
}

function toggleDetails() {
    const isChecked = document.getElementById('recursion-toggle').checked;
    const stackBox = document.getElementById('stack-box');
    const recBox = document.getElementById('recursion-details');
    if (isChecked) {
        stackBox.classList.remove('details-hidden');
        recBox.classList.remove('details-hidden');
    } else {
        stackBox.classList.add('details-hidden');
        recBox.classList.add('details-hidden');
    }
}

document.getElementById('run-btn').addEventListener('click', run);
document.getElementById('clear-btn').addEventListener('click', resetAll);
document.getElementById('recursion-toggle').addEventListener('change', toggleDetails);
toggleDetails();